import { useMemo } from "react";
import { Link } from "react-router-dom";
import { getFlagSvgUrl, getFlag } from "@/lib/country-flags";
import { useCountryRates } from "@/hooks/useCountryRates";
import { getRegionalCountryCodes, getRegion } from "@/config/country-regions";
import { CONNECT_COUNTRIES } from "@/config/connect-countries";
import { cn } from "@/lib/utils";

interface CountrySelectorBarProps {
  currentCountry: string; // slug e.g. "south-sudan"
  className?: string;
  /** Price the neighbours in the same list as the page (corridor pages pass USD). */
  currency?: "AUD" | "USD";
}

export function CountrySelectorBar({ currentCountry, className, currency }: CountrySelectorBarProps) {
  const { all, formatRate, showGst } = useCountryRates(currency ? { currency } : {});

  // Find the current country's ISO-2 code from config
  const currentConfig = CONNECT_COUNTRIES[currentCountry];
  const currentIso2 = currentConfig?.code || "";

  const nearbyWithRates = useMemo(() => {
    if (!currentIso2 || all.length === 0) return [];

    const regionalCodes = getRegionalCountryCodes(currentIso2);

    // Match rates by country name → ISO-2 mapping (rates use dial codes)
    // Build a name-to-rate lookup
    const ratesByName: Record<string, typeof all[0]> = {};
    for (const r of all) {
      ratesByName[r.countryName.toLowerCase()] = r;
    }

    // Try to find rates for regional countries using Intl display names
    const results: { iso2: string; name: string; rate: number; flag: string; dialCode: string; slug: string | null }[] = [];

    for (const code of regionalCodes) {
      // Get country name from Intl API
      let name = code;
      try {
        const dn = new Intl.DisplayNames(["en"], { type: "region" });
        name = dn.of(code) || code;
      } catch {
        // fallback
      }

      const rate = ratesByName[name.toLowerCase()];
      if (!rate) continue;

      // Find matching slug in CONNECT_COUNTRIES
      const slug = Object.entries(CONNECT_COUNTRIES).find(
        ([, c]) => c.code === code
      )?.[0] || null;

      results.push({
        iso2: code,
        name: rate.countryName,
        rate: rate.ratePerMinuteCents,
        flag: rate.flag,
        dialCode: rate.countryCode,
        slug,
      });
    }

    // Sort by lowest rate first
    results.sort((a, b) => a.rate - b.rate);

    // Limit to 8 results
    return results.slice(0, 8);
  }, [currentIso2, all]);

  if (nearbyWithRates.length === 0) return null;

  return (
    <div className={cn("py-6 bg-muted/30", className)}>
      <div className="container mx-auto">
        <p className="text-center text-base text-muted-foreground mb-4">
          Nearby destinations
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          {nearbyWithRates.map((item) => {
            const isActive = currentIso2 === item.iso2;
            const flagUrl = getFlagSvgUrl(
              getFlag({ dialCode: item.dialCode, countryName: item.name, flagEmoji: item.flag })
            );

            const linkTo = item.slug
              ? `/connect-portal/${item.slug}`
              : `/connect-portal/rates`;

            return (
              <Link
                key={item.iso2}
                to={linkTo}
                className={cn(
                  "inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "bg-background hover:bg-primary/10 hover:text-primary border"
                )}
              >
                <img
                  src={flagUrl}
                  alt={item.name}
                  className="w-5 h-4 object-cover rounded-sm"
                />
                <span>{item.name}</span>
                <span className="text-xs opacity-70">
                  {formatRate(item.rate)}/min
                </span>
              </Link>
            );
          })}
        </div>



      </div>
    </div>
  );
}
